import * as _ from 'lodash';
import * as uuid from 'uuid';
import {
    QTYPES,
    ProjectLanguage,
    Question,
    Option,
    Region,
    QuestionID,
    QuestionSummary,
    MULTIMEDIA_TYPES,
    Project,
    PanelType,
    SPECIAL_QIDS
} from '@nexxt/common/types';
import {
    CUSTOM_QUESTION_IDENTIFIER,
    LANGUAGE_QVALUE,
    BUILTIN_OPTION_ID,
    VECTOR_MAP_REGION_CODES,
    ICON_OPTION_VALUE,
    DYNATA_VMAP_MAPPING,
    DEVICE_TYPE_QVALUE,
    URL_METADATA_QUESTION_VALUE,
    PANEL_QVALUE
} from '@nexxt/common/constants';
import { BUILT_IN_TARGET_QVALUE } from '@nexxt/common/demographics/types';

export function getRichTextFromQuestionTexts(
    questionSummary: QuestionSummary,
    languages: ProjectLanguage[]
) {
    let richText;
    const questionTextsObject = {};
    questionSummary &&
        (questionSummary.questionTexts || []).forEach((texts) => {
            questionTextsObject[texts.language] = texts;
        });
    // @Todo: change this to proper code
    try {
        languages.forEach((l) => {
            if (!richText) {
                richText =
                    (questionTextsObject[l.value] &&
                        questionTextsObject[l.value].text) ||
                    questionTextsObject[l.value].richTexts ||
                    questionTextsObject[l.value].contents.filter(
                        (content) => content.type === MULTIMEDIA_TYPES.RICH_TEXT
                    )[0].richText ||
                    null;
            }
        });
    } catch (e) {
        richText = null;
    }
    if (typeof richText === 'string') {
        richText = null;
    }
    /** Keep this for the previous version of questionTexts */
    richText = Array.isArray(richText) ? richText[0] : richText;
    return richText;
}
/**
 * Sort questions by collections(parent question id). Prompt when a user dnd a question card in the parent group.
 * Returns new array of QuestionSumamry[]
 * @param questionObject
 * @param questionId
 * @param sortableQuestion
 * @param dragIndex
 * @param hoverIndex
 * @param collection
 */
export function sortByIndexRecursive(
    questionObject: { [questionValue: string]: QuestionSummary },
    questionId: number,
    questionSummaries: QuestionSummary[],
    dragIndex: number,
    hoverIndex: number,
    collection: number
): QuestionSummary[] {
    const parentIds: QuestionID[] = getRootQuestion(questionObject, questionId);
    return sortableRecursive(
        questionSummaries,
        parentIds,
        dragIndex,
        hoverIndex,
        collection
    );
}

export function sortableRecursive(
    questionSummaries: QuestionSummary[],
    questionIds: QuestionID[],
    dragIndex: number,
    hoverIndex: number,
    collection: number
): QuestionSummary[] {
    const copyQuestionSummaries = _.cloneDeep(questionSummaries);
    if (questionIds.length === 1) {
        const currentQuestionId = questionIds.shift();
        return copyQuestionSummaries.map((m, index) => {
            if (m.id === currentQuestionId) {
                const childrenSummaries = sortQuestion(
                    m.childrenSummaries,
                    dragIndex,
                    hoverIndex,
                    0
                );
                return {
                    ...m,
                    childrenSummaries,
                    childrenIds: childrenSummaries.map((child) => child.id)
                };
            } else {
                return m;
            }
        });
    } else {
        const currentQuestionId = questionIds.shift();
        return copyQuestionSummaries.map((m, index) => {
            if (m.id === currentQuestionId) {
                const childrenSummaries = sortableRecursive(
                    m.childrenSummaries,
                    questionIds,
                    dragIndex,
                    hoverIndex,
                    collection
                );
                return {
                    ...m,
                    childrenSummaries,
                    childrenIds: childrenSummaries.map((child) => child.id)
                };
            } else {
                return m;
            }
        });
    }
}
/**
 * Actual sort happens here based on a user's action
 * @param sortableQuestion
 * @param dragIndex
 * @param hoverIndex
 * @param collections
 */
export function sortQuestion(
    questionSummaries: QuestionSummary[],
    dragIndex: number,
    hoverIndex: number,
    collections: number
): QuestionSummary[] {
    const copyQuestionSummaries = _.cloneDeep(questionSummaries);
    const dragCard = {
        ...questionSummaries[dragIndex]
    };
    copyQuestionSummaries.splice(dragIndex, 1);
    copyQuestionSummaries.splice(hoverIndex, 0, dragCard);
    return copyQuestionSummaries.map((q, index) => {
        return { ...q, position: index };
    });
}
// Get parent IDs in each level from its parent to the question staying at the root level
// i.e. if you moved one of the question in the SON group question, [GRANDPARENT, PARENT, SON]
function getRootQuestion(
    questionObject: { [questionValue: string]: QuestionSummary },
    questionId: QuestionID,
    arr?: QuestionID[]
): QuestionID[] {
    const result = arr || [];
    const currentQuestion = questionObject[questionId];
    if (currentQuestion.parent !== 0) {
        result.unshift(currentQuestion.id);
        return getRootQuestion(questionObject, currentQuestion.parent, result);
    } else {
        result.unshift(questionId);
        return result;
    }
}

/**
 * Get a single array of question lists parsed from QuestionSummary Object.
 * Currently being used in Group/Multimedia question and condition.
 * @param questions
 */
export function parseQuestionSummaryToObjects(
    questionSummaries: QuestionSummary[]
): { [value: string]: QuestionSummary } {
    let questionObjects: { [value: string]: QuestionSummary } = {};
    (questionSummaries || []).forEach((questionSummary) => {
        questionObjects[questionSummary.value || questionSummary.id] = {
            ...questionSummary,
            questionTexts: (questionSummary.questionTexts || []).map((qt) => ({
                ...qt
            }))
        };
        if (questionSummary?.childrenSummaries?.length) {
            questionObjects = {
                ...questionObjects,
                ...parseQuestionSummaryToObjects(
                    questionSummary?.childrenSummaries
                )
            };
        }
    });
    return questionObjects;
}

/**
 * returns same structure as QuestionSummary.
 * contains all questions regardless of parent and child relationship
 * being used in Condition
 * @param questionSummary
 */
export function flattenQuestionSummary(
    questionSummaries: QuestionSummary[]
): QuestionSummary[] {
    let newQuestionSummaries: QuestionSummary[] = [];

    (questionSummaries || [])
        .filter((x) => x)
        .forEach((questionSummary) => {
            newQuestionSummaries = [...newQuestionSummaries, questionSummary];

            if (
                questionSummary.childrenSummaries &&
                questionSummary.childrenSummaries.length
            ) {
                newQuestionSummaries = [
                    ...newQuestionSummaries,
                    ...flattenQuestionSummary(questionSummary.childrenSummaries)
                ];
            }
        });
    return newQuestionSummaries;
}

/**
 * Parse Question type to QuestionSummary
 */
function parseQuestionToQuestionSummary(question: Question): QuestionSummary {
    return {
        childrenIds: [],
        childrenSummaries: [],
        id: question.id,
        isGroup: [QTYPES.POPUP, QTYPES.GROUP].includes(question.type),
        label: question.title,
        parent: question.parent || 0,
        position: question.position || 0,
        questionTexts: question.questionTexts || [],
        type: question.type,
        value: question.value,
        title: question.title,
        isPiping: false,
        hasCondition: question.type === QTYPES.VIRTUAL
    };
}

export function getQuestionSummariesWithBuiltInByProjectSettings(
    questionSummaries: QuestionSummary[],
    project: Project,
    url = true
) {
    const language = project?.languages?.length > 1,
        panel = project?.panelSettings?.panelType !== PanelType.INCA,
        device = project?.devices?.length > 1;

    return getQuestionSummariesWithBuiltIn(questionSummaries, {
        language,
        url,
        panel,
        device
    });
}
/**
 * Returns list of specified @questionSummaries with extra summaries for builtin questions
 * based on @settings (whichever builtin questions are set to true are included, appended to list).
 */
export function getQuestionSummariesWithBuiltIn(
    questionSummaries: QuestionSummary[],
    settings: {
        language?: boolean;
        panel?: boolean;
        device?: boolean;
        url?: boolean;
    }
): QuestionSummary[] {
    let _copyQuestionSummaries = questionSummaries;
    const systemMetadataOptions = [];
    if (settings?.url) {
        _copyQuestionSummaries = [
            ..._copyQuestionSummaries,
            getUrlMetadataQuestionSummary()
        ];
    }

    if (settings.language)
        systemMetadataOptions.push(getLanguageQuestionSummary());

    if (settings.panel)
        systemMetadataOptions.push(getSelfProvidedPanelQuestionSummary());

    if (settings?.device)
        systemMetadataOptions.push(getDeviceQuestionSummary());

    if (systemMetadataOptions.length) {
        _copyQuestionSummaries = [
            ..._copyQuestionSummaries,
            {
                label: 'System Metadata',
                options: systemMetadataOptions,
                type: QTYPES.SYSTEM_METADATA
            } as any
        ];
    }
    return _copyQuestionSummaries;
}

/**
 * Returns mapping of system question values to their respective QuestionSummary.
 * @TODO: could be consolidated with getQuestionSummariesWithBuiltIn
 */
export function getSystemQuestionSummaries(): {
    [systemQuestionValue: string]: QuestionSummary;
} {
    return {
        [LANGUAGE_QVALUE]: getLanguageQuestionSummary(),
        [DEVICE_TYPE_QVALUE]: getDeviceQuestionSummary(),
        [URL_METADATA_QUESTION_VALUE]: getUrlMetadataQuestionSummary(),
        [PANEL_QVALUE]: getSelfProvidedPanelQuestionSummary()
    };
}
/**
 * Add language selection as a question for condition
 */
function getLanguageQuestionSummary(): QuestionSummary {
    return {
        childrenIds: [],
        childrenSummaries: [],
        id: SPECIAL_QIDS.LANGUAGE,
        isGroup: false,
        label: 'Language',
        parent: 0,
        position: 0,
        questionTexts: [],
        type: QTYPES.LANGUAGE,
        value: LANGUAGE_QVALUE,
        title: LANGUAGE_QVALUE,
        isPiping: false
    };
}

function getDeviceQuestionSummary(): QuestionSummary {
    return {
        childrenIds: [],
        childrenSummaries: [],
        id: SPECIAL_QIDS.DEVICE,
        isGroup: false,
        label: 'Device Type',
        parent: 0,
        position: 0,
        questionTexts: [],
        type: QTYPES.DEVICE,
        value: DEVICE_TYPE_QVALUE,
        title: DEVICE_TYPE_QVALUE,
        isPiping: false
    };
}

function getUrlMetadataQuestionSummary(): QuestionSummary {
    return {
        childrenIds: [],
        childrenSummaries: [],
        id: SPECIAL_QIDS.URL_METADATA,
        isGroup: false,
        label: 'URL Metadata',
        parent: 0,
        position: 0,
        questionTexts: [],
        type: QTYPES.URL_METADATA,
        value: URL_METADATA_QUESTION_VALUE,
        title: URL_METADATA_QUESTION_VALUE,
        isPiping: false
    };
}

function getSelfProvidedPanelQuestionSummary(): QuestionSummary {
    return {
        childrenIds: [],
        childrenSummaries: [],
        id: SPECIAL_QIDS.PANEL_SOURCE,
        isGroup: false,
        label: 'Self-provided panel',
        parent: 0,
        position: 0,
        questionTexts: [],
        type: QTYPES.PANEL,
        value: PANEL_QVALUE,
        title: PANEL_QVALUE,
        isPiping: false
    };
}

export function getFlowAfterQuestionRemoval(
    questionSummaries: QuestionSummary[],
    questionID: number
): QuestionSummary[] {
    const newQuestionFlow = [
        ...questionSummaries.filter((questionSummary) => {
            if (questionSummary?.childrenSummaries) {
                const newChildrenSummaries = getFlowAfterQuestionRemoval(
                    questionSummary.childrenSummaries,
                    questionID
                );

                questionSummary.childrenSummaries = newChildrenSummaries;
                questionSummary.childrenIds = newChildrenSummaries.map(
                    (child) => child.id
                );
            }
            return questionSummary.id != questionID;
        })
    ];
    return newQuestionFlow.map((flow, i) => ({ ...flow, position: i }));
}

// Get state/province as built-in an option
export function getVectorMapOptions(question: Question): Option[] {
    const identifier = (question?.selectedRegion as Region).identifier || '';
    let options = [];
    if (identifier) {
        const regionList = Object.keys(VECTOR_MAP_REGION_CODES).filter(
            (code) => code.split('/')[3] === identifier
        );
        options = regionList.map((region) => {
            const value = DYNATA_VMAP_MAPPING[region] || region;
            return {
                id: BUILTIN_OPTION_ID,
                label: VECTOR_MAP_REGION_CODES[region],
                // DYNATA MAPPING FOR DYNATA ONLY(FOR US / CA)
                value,
                optionKey: `${question.id}_${value}`
            };
        });

        options.sort((a, b) =>
            a.label < b.label ? -1 : a.label > b.label ? 1 : 0
        );
    }
    return options;
}

// Get icon scale as an built-in option
export function getIconScaleOptions(question: Question): Option[] {
    const numberOptions = question.iconNumber;
    let options = [];
    for (let i = 1; i <= numberOptions; i++) {
        const value = ICON_OPTION_VALUE[i - 1];
        options = [
            ...options,
            {
                label: i,
                value,
                optionKey: `${question.id}_${value}`,
                id: BUILTIN_OPTION_ID
            }
        ];
    }
    return options;
}

// Reset the key of the copied Editor object in the questionTexts
// This is hacky solution to update key
export function resetKey(questionTexts) {
    const newQuestionTexts = questionTexts.map((b) => {
        const oldRichText = b.contents[0].richText;
        const richText = _.cloneDeep(oldRichText);
        richText.blocks.forEach((block) => {
            const newKey = Math.random().toString(36).substr(2, 5);

            block = { ...block, key: newKey };
        });
        return {
            ...b,
            contents: [
                { id: uuid.v4(), type: MULTIMEDIA_TYPES.RICH_TEXT, richText }
            ]
        };
    });
    return newQuestionTexts;
}

/**
 * This function will be called after `api/questions/ (update a single question)
 * in order to propagate those changes to the questionSummaries (in question reducer)
 * so that the properties for the updated question are reflected in the questionSummaries
 * @param questionSummary
 * @param question
 */
export function updateQuestionSummary(
    questionSummary: QuestionSummary[],
    question: Question
): QuestionSummary[] {
    const _questionSummary = _.cloneDeep(questionSummary);
    return _questionSummary.map((q) => {
        if (q.id === question.id) {
            q.label = question.title;
            q.title = question.title;
            q.questionTexts = question.questionTexts;
            q.hasCondition = question.hasCondition || false;
            q.type = question.type;
            q.audienceSettings = question?.audienceSettings || {};
            if (q.type === QTYPES.POPUP) {
                q.childrenSummaries = question.childrenSummaries;
                q.childrenIds = question.childrenIds;
            }
        } else if (q.childrenSummaries.length) {
            const childrenSummaries = updateQuestionSummary(
                q.childrenSummaries,
                question
            );
            q.childrenSummaries = childrenSummaries;
            q.childrenIds = childrenSummaries.map((c) => c.id);
        }
        return q;
    });
}
/**
 * This function will be called recursively after saving a new question.
 * This is for re-ordering the question positions based on the index of QuestionSummaries.
 * If the new question were not in the root level, this function will be called again to order within the children level
 *
 * @param questionSummaries
 * @param newQuestion
 */
export function getFlowAfterQuestionCreation(
    questionSummaries: QuestionSummary[],
    newQuestion: QuestionSummary
): QuestionSummary[] {
    const parentId = newQuestion.parent;
    const _questionSummaries = _.cloneDeep(questionSummaries);
    // parent id = 0 -> the new question is under root level.
    // re-order the root level questions
    if (!parentId) {
        _questionSummaries.splice(newQuestion.position, 0, newQuestion);
        return _questionSummaries.map((qt, index) => ({
            ...qt,
            position: index
        }));
    }
    return _questionSummaries.map((qt) => {
        // if parentID equals to the question(qt) id, add the new question to the position and reorder its children
        if (qt.id === parentId) {
            qt.childrenSummaries.splice(newQuestion.position, 0, newQuestion);
            qt.childrenSummaries = qt.childrenSummaries.map((qtc, i) => ({
                ...qtc,
                position: i
            }));
            qt.childrenIds = qt.childrenSummaries.map((q) => q.id);
        } else if (qt.childrenSummaries.length) {
            // if qt has own children, call this function recursively
            const childrenSummaries = getFlowAfterQuestionCreation(
                qt.childrenSummaries,
                newQuestion
            );
            qt.childrenSummaries = childrenSummaries;
            qt.childrenIds = childrenSummaries.map((c) => c.id);
        }
        return qt;
    });
}

export function getQuestionIdsByBuiltInIds(
    questionSummaries: QuestionSummary[]
): { [key: string]: QuestionID } {
    const builtInIds = Object.values(BUILT_IN_TARGET_QVALUE);
    const questionIdObject = builtInIds.reduce((arr, value) => {
        const questionID = questionSummaries.find(
            (q) => q.builtInID === value
        )?.id;
        arr = { ...arr, [value]: questionID };
        return arr;
    }, {});
    return questionIdObject;
}

export function getFilteredQuestionTitles(
    questionTitles: string[] = [],
    question: Question
): string[] {
    if (question?.id) {
        const removeTitle = (currTitle) => {
            const currTitleIndex = questionTitles?.findIndex(
                (title) => title === currTitle?.toLowerCase()
            );
            if (currTitleIndex > -1) {
                questionTitles.splice(currTitleIndex, 1);
            }
        };

        if (question.type === QTYPES.POPUP) {
            const skipTitles = [
                question.title,
                question.setupQuestion.title,
                ...question.popupCards.map((card) => card.title)
            ];
            skipTitles.forEach((title) => removeTitle(title));
        } else {
            removeTitle(question.title);
        }
    }

    return questionTitles;
}

/**
 * When deleting a question that is currently selected OR cancelling creating a question, it will take you the adjacent question.
 * The adjacent question can be any of the following, in descending order (i.e., try the first one first, if that’s not available
 * then go on to the next one, and so on…): the previous question in same group (or at root-level), if available the next question in same group-level (or at root-level),
 * if available parent group, if no other questions in group greeting question, if no other questions in project
 * @param question
 * @param questionSummaries
 * @returns {number}
 */
export function getAdjacentQuestion(
    currentQuestion: Question,
    questionSummaries: QuestionSummary[]
): number {
    const { type, parent, position: currentPosition } = currentQuestion;
    const flattenedQuestionSummaries =
        flattenQuestionSummary(questionSummaries);

    const siblingQuestionSummaries = flattenedQuestionSummaries.filter(
        (q) => q.parent === parent
    );
    if ([QTYPES.POPUP_CARD, QTYPES.MULTI_POPUP_CARD].includes(type)) {
        if (siblingQuestionSummaries.length === 2) {
            return Number(parent);
        } else if (currentPosition === 1) {
            return Number(siblingQuestionSummaries[2].id);
        } else {
            return Number(siblingQuestionSummaries[currentPosition - 1].id);
        }
    } else {
        if (siblingQuestionSummaries.length === 1) {
            return Number(parent) || -1;
        } else if (currentQuestion.position) {
            return Number(siblingQuestionSummaries[currentPosition - 1].id);
        } else {
            return Number(siblingQuestionSummaries[1].id);
        }
    }
}
